import { useState } from "react";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { RotateCcw, Trash2 } from "lucide-react";
import type { DocumentTrashItem } from "../../shared/document-trash";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { FieldError } from "../components/ui/field";
import { listDeletedDocuments, recoverDocument } from "./document-trash-api";

const date = (seconds: number) =>
  new Date(seconds * 1000).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
export function DocumentRecycleBin({ slug }: { slug?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={<Button variant="ghost" size="sm" />}>
        <Trash2 /> Recycle bin
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Recycle bin</DialogTitle>
          <DialogDescription>
            Restore deleted documents within 30 days. After that, documents and their saved history
            are permanently removed.
          </DialogDescription>
        </DialogHeader>
        {open ? <BinContents {...(slug ? { slug } : {})} /> : null}
      </DialogContent>
    </Dialog>
  );
}
function BinContents({ slug }: { slug?: string }) {
  const client = useQueryClient();
  const query = useInfiniteQuery({
    queryKey: ["ops", "recycle-bin", { customerSlug: slug ?? null }],
    queryFn: ({ pageParam }) => listDeletedDocuments(slug, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });
  const [notice, setNotice] = useState("");
  const restore = useMutation({
    mutationFn: (item: DocumentTrashItem) => recoverDocument(item.id, slug),
    onSuccess: async (_, item) => {
      setNotice(`Restored ${item.title}.`);
      await client.invalidateQueries({ queryKey: ["ops"] });
    },
  });
  const documents = query.data?.pages.flatMap((page) => page.documents) ?? [];
  return (
    <>
      <DialogBody>
        {query.isPending ? <p role="status">Loading deleted documents…</p> : null}
        {query.error ? (
          <div>
            <FieldError>{query.error.message}</FieldError>
            <Button variant="ghost" onClick={() => void query.refetch()}>
              Try again
            </Button>
          </div>
        ) : null}
        {restore.error ? <FieldError>{restore.error.message}</FieldError> : null}
        {notice ? (
          <p role="status" className="text-sm">
            {notice}
          </p>
        ) : null}
        {query.isSuccess && !documents.length ? (
          <p className="py-8 text-center text-muted-foreground">Your recycle bin is empty.</p>
        ) : null}
        <DeletedItems
          documents={documents}
          busy={restore.isPending}
          onRestore={(item) => {
            restore.mutate(item);
          }}
        />
        {query.hasNextPage ? (
          <Button
            variant="ghost"
            disabled={query.isFetchingNextPage}
            onClick={() => void query.fetchNextPage()}
          >
            Load more
          </Button>
        ) : null}
      </DialogBody>
      <DialogFooter>
        <p className="text-sm text-muted-foreground">
          {slug
            ? "Restored documents are team-only. Share them again when ready."
            : "Restore a parent page before restoring its subpages."}
        </p>
      </DialogFooter>
    </>
  );
}

function DeletedItems({
  documents,
  busy,
  onRestore,
}: {
  documents: DocumentTrashItem[];
  busy: boolean;
  onRestore: (item: DocumentTrashItem) => void;
}) {
  return (
    <ul className="divide-y">
      {documents.map((item) => (
        <li key={item.id} className="flex flex-wrap items-center justify-between gap-3 py-4">
          <div className="min-w-0 flex-1">
            <p className="break-words font-semibold">{item.title}</p>
            <p className="text-sm text-muted-foreground">
              Deleted {date(item.deletedAt)} by {item.deletedBy}
            </p>
            <p className="text-sm">Permanently deleted after {date(item.purgeAfter)}</p>
          </div>
          {item.canRestore ? (
            <Button
              variant="secondary"
              disabled={busy}
              aria-label={`Restore ${item.title}`}
              onClick={() => {
                onRestore(item);
              }}
            >
              <RotateCcw /> Restore
            </Button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
